import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PopupNotificationsService } from '@principle-theorem/ng-interactions';
import {
  IToolbarAlert,
  OrganisationService,
  StateBasedNavigationService,
} from '@principle-theorem/ng-principle-shared';
import { IDateFormData } from '@principle-theorem/ng-reporting';
import {
  BasicDialogService,
  DialogPresets,
  StorageResponseAPI,
} from '@principle-theorem/ng-shared';
import { PopupNotification } from '@principle-theorem/notifications';
import {
  Brand,
  BrandLinks,
  CampaignCostEstimate,
  CampaignEntryEnrolment,
  MarketingCampaign,
  MarketingCampaignRun,
  OrganisationLinks,
  ReportRun,
  Staffer,
  TimezoneResolver,
} from '@principle-theorem/principle-core';
import {
  BULK_ACTION_PAGE_SIZE,
  CampaignBulkActionStatus,
  CampaignBulkActionType,
  ICampaignBulkAction,
  ICampaignBulkActionPage,
  ICampaignEntryReportResult,
  ICustomReport,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IReportRun,
  MarketingCampaignRunEnrollmentMethod,
  MarketingCampaignRunStatus,
  MarketingCampaignRunType,
  MarketingCampaignStepStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  BigQueryDataSource,
  CampaignEntryReport,
  CampaignEntryReportContext,
  CustomReportFilter,
  CustomReportTemplate,
  ICampaignEntryReportContext,
  ICustomReportTemplate,
} from '@principle-theorem/reporting';
import {
  asReffable,
  DocumentReference,
  Firestore,
  getError,
  IReffable,
  ITimestampRange,
  multiSwitchMap,
  orderBy,
  resolveDocRefs,
  resolveWithConcurrency,
  snapshot,
  snapshotDefined,
  to24hrTime,
  toTimePeriod,
  toTimestamp,
  toTimestampRange,
  uid,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { chunk } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AddCampaignDialogComponent,
  IAddCampaignDialogResponse,
} from './add-campaign-dialog/add-campaign-dialog.component';
import {
  ApproveEntriesDialogComponent,
  IApproveEntriesDialogRequest,
  IApproveEntriesDialogResponse,
} from './approve-entries-dialog/approve-entries-dialog.component';
import {
  CampaignEnrolmentSettingsDialogComponent,
  ICampaignEnrolmentSettingsDialogRequest,
  ICampaignEnrolmentSettingsDialogResponse,
} from './campaign-enrolment-settings-dialog/campaign-enrolment-settings-dialog.component';
import {
  CampaignEntrySettingsDialogComponent,
  ICampaignEntrySettingsDialogRequest,
  ICampaignEntrySettingsDialogResponse,
} from './campaign-entry-settings-dialog/campaign-entry-settings-dialog.component';
import {
  CampaignNextRunDateSelectorDialogComponent,
  ICampaignNextRunDateSelectorResult,
  IMarketingCampaignNextRunDialogData,
} from './campaign-next-run-date-selector-dialog/campaign-next-run-date-selector-dialog.component';
import {
  CampaignRunReportDialogComponent,
  ICampaignRunReportDialogRequest,
  ICampaignRunReportDialogResponse,
} from './campaign-run-report-dialog/campaign-run-report-dialog.component';
import {
  CampaignRunUploadDialogComponent,
  ICampaignRunUploadDialogRequest,
  ICampaignRunUploadDialogResponse,
} from './campaign-run-upload-dialog/campaign-run-upload-dialog.component';
import {
  CampaignRunUploadHelpDialogComponent,
  ICampaignRunUploadHelpDialogRequest,
} from './campaign-run-upload-help-dialog/campaign-run-upload-help-dialog.component';
import {
  CampaignSettingsDialogComponent,
  ICampaignSettingsDialogRequest,
  ICampaignSettingsDialogResponse,
} from './campaign-settings-dialog/campaign-settings-dialog.component';
import {
  ISelectCampaignReportDialogRequest,
  ISelectCampaignReportDialogResponse,
  SelectCampaignReportDialogComponent,
} from './select-campaign-report-dialog/select-campaign-report-dialog.component';

interface IBulkActionLabel {
  gerund: string;
  infinitive: string;
}

const BULK_ACTION_LABELS: Record<CampaignBulkActionType, IBulkActionLabel> = {
  [CampaignBulkActionType.Approve]: {
    gerund: 'Approving',
    infinitive: 'approve',
  },
  [CampaignBulkActionType.Remove]: {
    gerund: 'Removing',
    infinitive: 'remove',
  },
  [CampaignBulkActionType.Unenrol]: {
    gerund: 'Unenrolling',
    infinitive: 'unenrol',
  },
};

@Injectable()
export class MarketingCampaignService {
  private _organisation = inject(OrganisationService);
  private _stateNav = inject(StateBasedNavigationService);
  private _dialog = inject(BasicDialogService);
  private _snackBar = inject(MatSnackBar);
  private _storageResponseApi = inject(StorageResponseAPI);
  private _popupNotifications = inject(PopupNotificationsService);

  async addCampaignFromReport(
    customReportRef: DocumentReference<ICustomReport>
  ): Promise<void> {
    const response = await this._dialog
      .open<
        CampaignSettingsDialogComponent,
        ICampaignSettingsDialogRequest,
        ICampaignSettingsDialogResponse
      >(
        CampaignSettingsDialogComponent,
        DialogPresets.medium({ autoFocus: true, data: {} })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    const campaign = response.campaign;
    campaign.customReportRef = customReportRef;

    const campaignRef = await Firestore.addDoc(
      Brand.marketingCampaignCol(asReffable(campaign.brandRef)),
      campaign
    );

    this._snackBar.open('Campaign added');
    const link = BrandLinks.marketingCampaign(campaignRef);
    await this._stateNav.brand(link.segments, {
      fragment: link.fragment,
    });
  }

  async addCampaign(): Promise<void> {
    const response = await this._dialog
      .open<AddCampaignDialogComponent, void, IAddCampaignDialogResponse>(
        AddCampaignDialogComponent,
        DialogPresets.large({ autoFocus: true })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    this._snackBar.open('Campaign added');
    const link = BrandLinks.marketingCampaign(response.campaignRef);
    await this._stateNav.brand(link.segments, {
      fragment: link.fragment,
    });
  }

  async editCampaignSettings(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const response = await this._dialog
      .open<
        CampaignSettingsDialogComponent,
        ICampaignSettingsDialogRequest,
        ICampaignSettingsDialogResponse
      >(
        CampaignSettingsDialogComponent,
        DialogPresets.medium({ data: { campaign }, autoFocus: true })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    await Firestore.saveDoc({ ref: campaign.ref, ...response.campaign });
    this._snackBar.open('Campaign updated');
  }

  async editCampaignEntrySettings(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const data: ICampaignEntrySettingsDialogRequest = { campaign };
    const response = await this._dialog
      .open<
        CampaignEntrySettingsDialogComponent,
        ICampaignEntrySettingsDialogRequest,
        ICampaignEntrySettingsDialogResponse
      >(
        CampaignEntrySettingsDialogComponent,
        DialogPresets.medium({ data, autoFocus: true })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    await Firestore.saveDoc({
      ...campaign,
      entryResetRules: response.formData.entryResetRules,
      scope: response.formData.scope,
      conversionType: response.formData.conversionType,
    });
    this._snackBar.open('Campaign entry settings updated');
  }

  async editCampaignEnrolmentSettings(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const data: ICampaignEnrolmentSettingsDialogRequest = { campaign };
    const response = await this._dialog
      .open<
        CampaignEnrolmentSettingsDialogComponent,
        ICampaignEnrolmentSettingsDialogRequest,
        ICampaignEnrolmentSettingsDialogResponse
      >(
        CampaignEnrolmentSettingsDialogComponent,
        DialogPresets.medium({ data, autoFocus: true })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    await Firestore.saveDoc({
      ...campaign,
      recurrencePattern: response.formData.recurrencePattern,
      recurrenceRunTime: response.formData.recurrenceRunTime,
      maximumEntriesPerRun: response.formData.maximumEntriesPerRun,
    });
    this._snackBar.open('Campaign enrolment settings updated');
  }

  async dismissConfigureReportPrompt(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: `Configure Campaign Report`,
      prompt:
        'If you choose not to configure a custom report, this campaign cannot be automated. New campaign runs must be handled manually.',
      submitLabel: `I don't want to configure a report`,
      cancelLabel: 'Cancel',
    });
    if (!confirmed) {
      return;
    }
    await Firestore.patchDoc(campaign.ref, {
      dismissedCustomReportPrompt: true,
    });
  }

  async removeCustomReport(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Remove Campaign Report',
      prompt:
        'Automated reports will no longer be generated for this campaign. New campaign runs must be handled manually. Are you sure you want to remove the report?',
      submitLabel: 'Remove Report',
      submitColor: 'warn',
    });

    if (!confirmed) {
      return;
    }

    await Firestore.saveDoc({
      ...campaign,
      customReportRef: undefined,
    });
    this._snackBar.open('Removed Campaign Report');
  }

  async selectCustomReport(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const data: ISelectCampaignReportDialogRequest = {
      campaign,
    };
    const response = await this._dialog
      .open<
        SelectCampaignReportDialogComponent,
        ISelectCampaignReportDialogRequest,
        ISelectCampaignReportDialogResponse
      >(
        SelectCampaignReportDialogComponent,
        DialogPresets.extraLarge({
          minHeight: '90vh',
          maxHeight: '90vh',
          data,
        })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }

    if (response.option.customReport) {
      await Firestore.patchDoc(campaign.ref, {
        customReportRef: response.option.customReport.ref,
        dismissedCustomReportPrompt: true,
      });
      this._snackBar.open('Changed Campaign Report');
      return;
    }

    if (response.option.template) {
      return this.createCustomReport(campaign, response.option.template);
    }
  }

  async createCustomReport(
    campaign: WithRef<IMarketingCampaign>,
    template: ICustomReportTemplate
  ): Promise<void> {
    const brand = await snapshot(this._organisation.brand$);
    const staffer = await snapshot(this._organisation.staffer$);
    if (!brand || !staffer) {
      this._snackBar.open('Unable to create custom report');
      return;
    }

    const adjustedTemplate: ICustomReportTemplate = {
      ...template,
      description: `${template.description}. Created for Marketing Campaign: ${campaign.name}`,
    };

    const reportRef = await Firestore.runTransaction(async (transaction) => {
      const customReportRef =
        await CustomReportTemplate.createReportFromTemplate(
          brand.ref,
          adjustedTemplate,
          staffer
        );
      await Firestore.patchDoc(
        campaign.ref,
        { customReportRef, dismissedCustomReportPrompt: true },
        transaction
      );
      return customReportRef;
    });

    this._snackBar.open('Custom Report Created');
    const link = OrganisationLinks.customReport(reportRef);
    await this._stateNav.organisation(link.segments, {
      fragment: link.fragment,
    });
  }

  getToolbarAlerts$(): Observable<IToolbarAlert[]> {
    return this._organisation.brand$.pipe(
      switchMap((brand) => {
        if (!brand) {
          return of([]);
        }
        return Brand.marketingAlerts$(brand).pipe(
          multiSwitchMap((campaignRun) =>
            this._pendingCampaignRunToAlert(campaignRun)
          )
        );
      })
    );
  }

  async managementDraft(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Revert Campaign Run',
      prompt: [
        `Are you sure you want to revert the campaign run back to draft?`,
      ],
      submitLabel: 'Revert to Draft',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    await Firestore.patchDoc(campaignRun.ref, {
      status: MarketingCampaignRunStatus.Draft,
    });
    this._snackBar.open('Campaign run reverted to draft');
  }

  async managementApprove(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Approve Campaign Run',
      prompt: [`Are you sure you want to approve the campaign run?`],
      submitLabel: 'Yes, start the campaign run',
      submitColor: 'warn',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    await Firestore.patchDoc(campaignRun.ref, {
      status: MarketingCampaignRunStatus.Pending,
    });
    this._snackBar.open('Campaign run approved');
  }

  async cancelCampaignRun(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    const confirmed = await this._dialog.confirm({
      title: 'Cancel Campaign Run',
      prompt: ['Are you sure you want to cancel this Campaign Run?'],
      submitLabel: 'Cancel Campaign Run',
      submitColor: 'warn',
    });
    if (!confirmed) {
      return;
    }
    await MarketingCampaignRun.cancel(campaignRun);
  }

  async getCampaignEntryReportData(
    campaign: WithRef<IMarketingCampaign>,
    customReport: WithRef<ICustomReport>,
    context: ICampaignEntryReportContext,
    formData: IDateFormData
  ): Promise<unknown[]> {
    const filterDateRange =
      formData.dateFrom && formData.dateTo
        ? toTimePeriod(formData.dateFrom, formData.dateTo)
        : undefined;

    const timezone = await TimezoneResolver.fromPracticeRef(
      campaign.practiceRef
    );

    const filters = CustomReportFilter.applyDatesToFilters(
      customReport.staticFilters,
      formData.date,
      filterDateRange
        ? toTimePeriod(filterDateRange.from, filterDateRange.to, timezone)
        : undefined
    );

    const reportingQuery = CampaignEntryReport.getReportingQuery(
      context,
      filters
    );
    if (!reportingQuery) {
      this._snackBar.open(
        'Unable to build report. Please check the custom report configuration.'
      );
      return [];
    }

    const queryScope = CampaignEntryReport.getQueryScope(campaign.practiceRef);
    const source = new BigQueryDataSource(
      this._storageResponseApi,
      of(queryScope.orgRef)
    );

    return source.build(reportingQuery, queryScope.scopeRequests, {
      customReportRef: customReport.ref,
      filterDate: formData.date,
      filterDateRange,
    });
  }

  async runCampaignEntryReport(
    campaign: WithRef<IMarketingCampaign>,
    formData: IDateFormData
  ): Promise<ICampaignEntryReportResult[]> {
    const customReport = campaign.customReportRef
      ? await Firestore.getDoc(campaign.customReportRef)
      : undefined;
    if (!customReport) {
      this._snackBar.open(
        'No custom report found for this campaign. Please select a custom report.'
      );
      return [];
    }
    const context = await CampaignEntryReportContext.buildFromCustomReport(
      campaign,
      customReport
    );

    if (!context) {
      this._snackBar.open(
        'Unable to build report. Please check the custom report configuration.'
      );
      return [];
    }
    const results = await this.getCampaignEntryReportData(
      campaign,
      customReport,
      context,
      formData
    );

    return CampaignEntryReport.getEligibleReportEntries(
      campaign,
      context,
      results
    );
  }

  async openReportEntryDialog(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    const campaign = await Firestore.getDoc(
      MarketingCampaignRun.campaignRef(campaignRun)
    );
    if (!campaign.customReportRef) {
      this._snackBar.open('No custom report found for this campaign.');
      return;
    }

    const customReport = await Firestore.getDoc(campaign.customReportRef);

    const response = await this._dialog
      .open<
        CampaignRunReportDialogComponent,
        ICampaignRunReportDialogRequest,
        ICampaignRunReportDialogResponse
      >(
        CampaignRunReportDialogComponent,
        DialogPresets.large({
          data: { campaign, campaignRun, customReport },
        })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    await this._enrolEntriesToCampaignRun(
      campaign,
      campaignRun,
      response.entries
    );

    const estimates = await CampaignCostEstimate.calculateEstimates(
      campaign,
      response.entries,
      campaign.maximumEntriesPerRun
    );

    const reportRunRef = await this._getLatestReportRunRef(campaign);

    await Firestore.patchDoc(campaignRun.ref, {
      enrolmentMethod: MarketingCampaignRunEnrollmentMethod.CustomReport,
      enrolmentDate: toTimestamp(),
      estimates,
      dateRange: this._inferDateRange(response.datesUsed),
      reportRunRef,
    });
  }

  async openFileEntryDialog(
    campaignRun: WithRef<IMarketingCampaignRun>,
    uploadedFiles: File[]
  ): Promise<void> {
    if (!uploadedFiles.length) {
      return;
    }
    const campaign = await Firestore.getDoc(
      MarketingCampaignRun.campaignRef(campaignRun)
    );

    const response = await this._dialog
      .open<
        CampaignRunUploadDialogComponent,
        ICampaignRunUploadDialogRequest,
        ICampaignRunUploadDialogResponse
      >(
        CampaignRunUploadDialogComponent,
        DialogPresets.large({
          data: { campaign, campaignRun, uploadedFiles },
        })
      )
      .afterClosed()
      .toPromise();

    if (!response) {
      return;
    }
    await this._enrolEntriesToCampaignRun(
      campaign,
      campaignRun,
      response.entries
    );

    const estimates = await CampaignCostEstimate.calculateEstimates(
      campaign,
      response.entries,
      campaign.maximumEntriesPerRun
    );
    await Firestore.patchDoc(campaignRun.ref, {
      enrolmentMethod: MarketingCampaignRunEnrollmentMethod.FileUpload,
      enrolmentDate: toTimestamp(),
      estimates,
    });
  }

  async openFileEntryInfo(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    await this._dialog
      .open<
        CampaignRunUploadHelpDialogComponent,
        ICampaignRunUploadHelpDialogRequest,
        void
      >(
        CampaignRunUploadHelpDialogComponent,
        DialogPresets.large({
          data: { campaign },
        })
      )
      .afterClosed()
      .toPromise();
  }

  async removeEntries(
    entries: WithRef<IMarketingCampaignEntry>[],
    campaign: IReffable<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    await this._executeBulkAction(
      entries,
      campaign,
      CampaignBulkActionType.Remove,
      campaignRun
    );
  }

  async approveEntries(
    entries: WithRef<IMarketingCampaignEntry>[],
    campaign: IReffable<IMarketingCampaign>,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    await this._executeBulkAction(
      entries,
      campaign,
      CampaignBulkActionType.Approve,
      campaignRun
    );
  }

  async unenrolEntries(
    entries: WithRef<IMarketingCampaignEntry>[],
    campaign: IReffable<IMarketingCampaign>,
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    await this._executeBulkAction(
      entries,
      campaign,
      CampaignBulkActionType.Unenrol,
      campaignRun
    );
  }

  async startCampaignRunPrompt(
    campaignRun: WithRef<IMarketingCampaignRun>,
    entries: WithRef<IMarketingCampaignEntry>[]
  ): Promise<WithRef<IMarketingCampaignEntry>[]> {
    const isDraft = campaignRun.status === MarketingCampaignRunStatus.Draft;
    if (!isDraft && entries.length <= 1) {
      return entries;
    }

    const response = await this._dialog
      .open<
        ApproveEntriesDialogComponent,
        IApproveEntriesDialogRequest,
        IApproveEntriesDialogResponse
      >(
        ApproveEntriesDialogComponent,
        DialogPresets.small({
          data: { campaignRun, entries },
        })
      )
      .afterClosed()
      .toPromise();

    const approvedEntries = response?.approvedEntries ?? [];
    if (!approvedEntries.length) {
      return [];
    }

    if (isDraft) {
      await Firestore.patchDoc(campaignRun.ref, {
        status: MarketingCampaignRunStatus.PendingPayment,
      });
    }

    return approvedEntries;
  }

  async updateNextRunDate(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<void> {
    const timezone = await TimezoneResolver.fromPracticeRef(
      campaign.practiceRef
    );

    const data: IMarketingCampaignNextRunDialogData = {
      nextRunAt: campaign.nextRunAt,
      timezone,
    };

    const result = await this._dialog
      .open<
        CampaignNextRunDateSelectorDialogComponent,
        IMarketingCampaignNextRunDialogData,
        ICampaignNextRunDateSelectorResult
      >(
        CampaignNextRunDateSelectorDialogComponent,
        DialogPresets.medium({
          data,
        })
      )
      .afterClosed()
      .toPromise();

    if (!result?.nextRunAt) {
      return;
    }

    await Firestore.patchDoc(campaign.ref, {
      nextRunAt: result.nextRunAt,
      recurrenceRunTime: to24hrTime(result.nextRunAt),
    });

    this._snackBar.open('Next run date updated');
  }

  async upsertDraftCampaignRun(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<DocumentReference<IMarketingCampaignRun>> {
    const campaignRunCol = MarketingCampaign.marketingCampaignRunCol(campaign);
    const campaignRuns = await Firestore.getDocs(campaignRunCol);
    const draftRun = campaignRuns
      .filter(undeletedFilter)
      .find((run) => run.status === MarketingCampaignRunStatus.Draft);

    if (draftRun) {
      return draftRun.ref;
    }

    return Firestore.addDoc(
      campaignRunCol,
      MarketingCampaignRun.init({ type: MarketingCampaignRunType.Manual })
    );
  }

  async openCampaignRun(
    campaignRunRef: DocumentReference<IMarketingCampaignRun>
  ): Promise<void> {
    const link = BrandLinks.marketingCampaignRun(campaignRunRef);
    await this._stateNav.brand(link.segments, { fragment: link.fragment });
  }

  private async _executeBulkAction(
    entries: WithRef<IMarketingCampaignEntry>[],
    campaign: IReffable<IMarketingCampaign>,
    action: CampaignBulkActionType,
    campaignRun?: WithRef<IMarketingCampaignRun>
  ): Promise<void> {
    if (!entries.length) {
      return;
    }

    const labels = BULK_ACTION_LABELS[action];
    const staffer = await snapshotDefined(this._organisation.staffer$);

    const notificationUid = uid();
    const notification = PopupNotification.init({
      title: `${labels.gerund} entries`,
      content: `${labels.gerund} 0/${entries.length} entries`,
      hasTimeout: false,
      uid: notificationUid,
    });
    const notificationRef = await Firestore.addDoc(
      Staffer.popupNotificationCol(staffer),
      notification,
      notificationUid
    );

    const entryRefChunks = chunk(
      entries.map((entry) => entry.ref),
      BULK_ACTION_PAGE_SIZE
    );

    const bulkActionRef = await Firestore.addDoc<ICampaignBulkAction>(
      MarketingCampaign.bulkActionCol(campaign),
      {
        campaignRunRef: campaignRun?.ref,
        action,
        pageCount: entryRefChunks.length,
        initiatedBy: staffer.ref,
        status: CampaignBulkActionStatus.Created,
        total: entries.length,
        processed: 0,
        deleted: false,
        deleteAfter: toTimestamp(moment().add(3, 'days')),
        notificationRef,
      }
    );

    try {
      await this._createBulkActionPages(bulkActionRef, entryRefChunks);
      await Firestore.patchDoc(bulkActionRef, {
        status: CampaignBulkActionStatus.Pending,
      });
    } catch (error) {
      await Firestore.patchDoc(bulkActionRef, {
        status: CampaignBulkActionStatus.Failed,
        errorMessage: getError(error),
        completedAt: toTimestamp(),
      });
      await Firestore.patchDoc(notificationRef, {
        title: `Failed to ${labels.infinitive} entries`,
        content: `An error occurred while ${labels.gerund.toLowerCase()} ${
          entries.length
        } entries`,
        hasTimeout: true,
      });
      throw error;
    }
  }

  private async _createBulkActionPages(
    bulkActionRef: DocumentReference<ICampaignBulkAction>,
    entryRefChunks: DocumentReference<IMarketingCampaignEntry>[][]
  ): Promise<void> {
    const deleteAfter = toTimestamp(moment().add(3, 'days'));
    const pageCol = MarketingCampaign.bulkActionPageCol(
      asReffable(bulkActionRef)
    );
    const indexedChunks = entryRefChunks.map((pageEntryRefs, index) => ({
      pageEntryRefs,
      index,
    }));
    await resolveWithConcurrency(
      indexedChunks,
      10,
      ({ pageEntryRefs, index }) => {
        const page: ICampaignBulkActionPage = {
          entryRefs: pageEntryRefs,
          status: CampaignBulkActionStatus.Created,
          processed: 0,
          total: pageEntryRefs.length,
          pageIndex: index,
          deleteAfter,
        };
        return Firestore.addDoc(pageCol, page);
      }
    );
  }

  private async _pendingCampaignRunToAlert(
    campaignRun: WithRef<IMarketingCampaignRun>
  ): Promise<IToolbarAlert> {
    const campaign = await Firestore.getDoc(
      MarketingCampaignRun.campaignRef(campaignRun)
    );
    return {
      label: 'Campaign Pending Payment',
      tooltip: `A run from '${campaign.name}' is pending payment. Please pay this invoice or contact us to proceed`,
      action: () => {
        const link = BrandLinks.marketingCampaignRun(campaignRun.ref);
        return void this._stateNav.brand(link.segments, {
          fragment: link.fragment,
        });
      },
    };
  }

  private async _enrolEntriesToCampaignRun(
    campaign: WithRef<IMarketingCampaign>,
    campaignRun: WithRef<IMarketingCampaignRun>,
    entries: ICampaignEntryReportResult[]
  ): Promise<void> {
    const staffer = await snapshotDefined(this._organisation.staffer$);

    const steps = await resolveDocRefs(campaign.steps);
    const activeSteps = steps.filter(
      (step) => step.status === MarketingCampaignStepStatus.Active
    );
    if (!activeSteps.length) {
      this._snackBar.open(
        'No active steps found for this campaign. Please add and activate steps to the campaign before enrolling entries.',
        'Dismiss',
        { duration: 10000 }
      );
      return;
    }

    const campaignRunMessage = (processed: number, total: number): string =>
      `Added ${processed}/${total} patients to campaign run`;

    const notification = PopupNotification.init({
      title: `Adding patients to campaign`,
      content: campaignRunMessage(0, entries.length),
      hasTimeout: false,
    });
    await this._popupNotifications.addNotification(notification, staffer.ref);

    const activeStepRefs = activeSteps
      .filter((step) => step.status === MarketingCampaignStepStatus.Active)
      .map((step) => step.ref);

    const enrolments = CampaignEntryEnrolment.evaluateEntries(
      entries,
      campaignRun.type,
      await MarketingCampaignRun.getAvailableEntryCount(campaignRun)
    );

    await resolveWithConcurrency(
      enrolments,
      100,
      async (enrolment) => {
        const patient = await CampaignEntryEnrolment.resolvePatient(enrolment);
        if (!patient) {
          return;
        }
        if (!CampaignEntryEnrolment.verifyEnrolment(patient, campaign.type)) {
          return;
        }
        const entry = CampaignEntryEnrolment.toEntry(
          enrolment,
          campaignRun.ref,
          activeStepRefs,
          patient
        );
        await Firestore.addDoc(
          MarketingCampaign.marketingCampaignEntryCol(campaign),
          entry
        );
      },
      (processed, total) => {
        if (processed >= total || processed % 100 === 0) {
          void this._popupNotifications.addNotification(
            {
              ...notification,
              content: campaignRunMessage(processed, total),
              hasTimeout: processed >= total,
            },
            staffer.ref
          );
        }
      }
    );

    if (!campaignRun.hasTrackedEnrolledEntries) {
      await Firestore.patchDoc(campaignRun.ref, {
        hasTrackedEnrolledEntries: true,
      });
    }
  }

  private async _getLatestReportRunRef(
    campaign: WithRef<IMarketingCampaign>
  ): Promise<DocumentReference<IReportRun> | undefined> {
    if (!campaign.customReportRef) {
      return;
    }
    const run = await Firestore.firstResult(
      ReportRun.col(asReffable(campaign.customReportRef)),
      orderBy('runAt', 'desc')
    );
    return run?.ref;
  }

  private _inferDateRange(
    datesUsed: IDateFormData
  ): ITimestampRange | undefined {
    if (datesUsed.dateFrom) {
      const dateTo =
        datesUsed.dateTo ?? moment(datesUsed.dateFrom).endOf('day');
      const dateRange = toTimePeriod(datesUsed.dateFrom, dateTo);
      return toTimestampRange(dateRange);
    }
    if (datesUsed.date) {
      const dateRange = toTimePeriod(
        moment(datesUsed.date).startOf('day'),
        moment(datesUsed.date).endOf('day')
      );
      return toTimestampRange(dateRange);
    }
  }
}
